import { createFileRoute, Link, notFound, redirect } from "@tanstack/react-router";
import { lastmodFor } from "@/lib/route-lastmod";
import { storyCanonicalAndAlternates } from "@/lib/hreflang";
import { ShareButtons } from "@/components/site/ShareButtons";
import { findStoryBySlug, largestUrl, resolveLegacyStorySlug, storyCredit, storyCreditSchema, storyImageObject, type Story } from "@/lib/results-stories";
import { PHYSICIAN_REF, ORG_REF } from "@/lib/medical-schema";
import { storyTitle, storyHeadline } from "@/lib/story-titles";

const SITE = "https://virginiabariatric.com";

function abs(url: string) {
  return url.startsWith("http") ? url : `${SITE}${url}`;
}

/** Truncate on a word boundary so descriptions never end mid-word. */
function clamp(text: string, max: number) {
  if (text.length <= max) return text;
  const cut = text.slice(0, max - 1);
  const space = cut.lastIndexOf(" ");
  return `${(space > max * 0.6 ? cut.slice(0, space) : cut).replace(/[.,;:\s]+$/, "")}…`;
}

/**
 * Append a suffix only when the whole sentence fits, so descriptions never end
 * on a half-truncated call to action.
 */
function withSuffix(caption: string, suffixes: string[], max: number) {
  const base = clamp(caption, max);
  const fitting = suffixes.find((s) => `${base} ${s}`.length <= max && base === caption);
  return fitting ? `${caption} ${fitting}` : base;
}

/**
 * Unique meta description per story — every caption in the data set is
 * distinct — kept under ~158 chars so Google doesn't truncate it.
 */
function storyDescription(story: Story) {
  return withSuffix(
    story.caption,
    ["See more before-and-after results from Dr. Matthew Fitzer.", "More before-and-after results.", "See more results."],
    158,
  );
}


export const Route = createFileRoute("/results/story/$slug")({
  loader: ({ params }) => {
    const found = findStoryBySlug(params.slug);
    if (!found) {
      // Old slug from a renamed story: 301 to the current URL so shared and
      // indexed links keep their SEO value. Unknown slugs stay a real 404.
      const current = resolveLegacyStorySlug(params.slug);
      if (current) {
        throw redirect({ to: "/results/story/$slug", params: { slug: current }, statusCode: 301 });
      }
      throw notFound();
    }
    return found;
  },
  head: ({ params, loaderData }) => {
    const story = loaderData?.story;
    if (!story) return {};
    const url = `${SITE}/results/story/${params.slug}`;
    const title = storyTitle(params.slug, story);
    const description = storyDescription(story);
    // og:* mirror the page title and description so the brand token search
    // engines resolve the site by is identical everywhere.
    const ogTitle = title;
    const ogDescription = description;
    const image = `${SITE}/og/stories/${params.slug}.jpg`;
    const photo = abs(largestUrl(story.single ? story.before : story.after));
    const alt = `${story.label} ${story.single ? "before and after" : "after"} weight-loss surgery with Dr. Matthew Fitzer at Virginia Bariatric Surgery`;

    return {
      meta: [
      { title },
      { name: "description", content: description },
      // Individual story pages are short, near-identical photo captions. They
      // stay live and shareable, but are kept out of the search index so the
      // /results gallery carries the ranking signal instead.
      { name: "robots", content: "noindex, follow" },
      ...(import.meta.env.SSR ? [
        { property: "og:site_name", content: "Virginia Bariatric Surgery" },
        { property: "og:title", content: ogTitle },
        { property: "og:description", content: description },
        { property: "og:url", content: url },
        { property: "og:type", content: "article" },
        { property: "og:locale", content: "en_US" },
        { property: "og:locale:alternate", content: "es_LA" },
        { property: "og:image", content: image },
        { property: "og:image:alt", content: alt },
        { property: "og:image:type", content: "image/jpeg" },
        { property: "og:image:width", content: "2400" },
        { property: "og:image:height", content: "1260" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: ogTitle },
        { name: "twitter:description", content: description },
        { name: "twitter:image", content: image },
        { name: "twitter:image:alt", content: alt },
      ] : []),
    ],
      links: [
        ...storyCanonicalAndAlternates(params.slug, "en"),
      ],
      scripts: import.meta.env.SSR ? [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "MedicalWebPage",
                "@id": `${url}#webpage`,
                workTranslation: { "@id": `${SITE}/es/antes-y-despues/historia/${params.slug}#webpage` },
                name: title,
                description,
                url,
                inLanguage: "en-US",
                specialty: "https://schema.org/Surgical",
                lastReviewed: "2026-08-09",
                reviewedBy: PHYSICIAN_REF,
                publisher: ORG_REF,
                about: { "@id": `${SITE}/#condition-obesity` },
                mainEntity: { "@id": `${url}#article` },
                primaryImageOfPage: { "@id": `${url}#primaryimage` },
                breadcrumb: { "@id": `${url}#breadcrumb` },
              },
              storyImageObject({
                id: `${url}#primaryimage`,
                img: story.single ? story.before : story.after,
                story,
                name: story.single
                  ? `${story.label} — before and after weight-loss surgery`
                  : `${story.label} — after weight-loss surgery`,
                caption: story.caption,
                representativeOfPage: true,
                isPartOf: `${url}#gallery`,
              }),
              {
                "@type": "ImageObject",
                "@id": `${url}#socialimage`,
                contentUrl: image,
                url: image,
                caption: alt,
                encodingFormat: "image/jpeg",
                width: 2400,
                height: 1260,
                ...storyCreditSchema(story),
              },
              ...(story.single
                ? []
                : [
                    storyImageObject({
                      id: `${url}#beforeimage`,
                      img: story.before,
                      story,
                      name: `${story.label} — before weight-loss surgery`,
                      caption: `${story.label} before weight-loss surgery with Dr. Matthew Fitzer.`,
                      isPartOf: `${url}#gallery`,
                    }),
                  ]),

              {
                "@type": "ImageGallery",
                "@id": `${url}#gallery`,
                name: `${storyHeadline(story.label)} — before and after photos`,
                isPartOf: { "@id": `${url}#webpage` },
                image: story.single
                  ? [{ "@id": `${url}#primaryimage` }]
                  : [{ "@id": `${url}#beforeimage` }, { "@id": `${url}#primaryimage` }],
              },
              {
                "@type": "CollectionPage",
                "@id": `${SITE}/results#webpage`,
                url: `${SITE}/results`,
                name: "Before & After | Virginia Bariatric",
              },
              {
                "@type": "Article",
                "@id": `${url}#article`,
                headline: storyHeadline(story.label),
                alternativeHeadline: story.tagline ?? undefined,
                description,
                url,
                mainEntityOfPage: { "@id": `${url}#webpage` },
                image: [{ "@id": `${url}#primaryimage` }, { "@id": `${url}#socialimage` }],
                author: PHYSICIAN_REF,
                publisher: ORG_REF,
                datePublished: "2026-08-09",
                dateModified: lastmodFor(`/results/story/${params.slug}`) ?? "2026-08-09",
                inLanguage: "en-US",
                articleSection: "Patient Results",
                genre: "Patient Results",
                keywords: [
                  "bariatric surgery before and after",
                  "weight loss surgery results",
                  "Northern Virginia bariatric surgeon",
                  story.label,
                ],
                articleBody: [story.caption, story.quote].filter(Boolean).join(" "),
                isPartOf: { "@id": `${SITE}/results#webpage` },
                associatedMedia: { "@id": `${url}#gallery` },
                about: { "@id": `${SITE}/#condition-obesity` },
                mentions: {
                  "@type": "MedicalProcedure",
                  name: "Bariatric surgery",
                  url: `${SITE}/services`,
                },
                ...(story.quote ? { citation: story.quote } : {}),
              },

              {
                "@type": "MedicalCondition",
                "@id": `${SITE}/#condition-obesity`,
                name: "Obesity",
                alternateName: ["Severe obesity", "Morbid obesity"],
                code: { "@type": "MedicalCode", codeValue: "E66", codingSystem: "ICD-10" },
                possibleTreatment: [
                  {
                    "@type": "MedicalProcedure",
                    name: "Sleeve gastrectomy",
                    url: `${SITE}/services/sleeve`,
                  },
                  {
                    "@type": "MedicalProcedure",
                    name: "Roux-en-Y gastric bypass",
                    url: `${SITE}/services/bypass`,
                  },
                  {
                    "@type": "MedicalTherapy",
                    name: "Weight loss medications",
                    url: `${SITE}/medical-weight-loss/medications`,
                  },
                ],
              },
            ],
          }),
        },
      ] : [],

    };
  },
  component: StoryPage,
});

function StoryPage() {
  const { story, slug } = Route.useLoaderData();
  const url = `${SITE}/results/story/${slug}`;

  return (
    <main className="mx-auto max-w-3xl px-6 py-10">
      <nav aria-label="Breadcrumb" className="text-sm text-muted-foreground">
        <Link to="/results" className="hover:underline">
          &larr; Before &amp; After
        </Link>
      </nav>

      <h1 className="mt-4 font-serif text-3xl md:text-4xl text-brand-logo">
        {storyHeadline(story.label)}
      </h1>
      {story.tagline && <p className="mt-2 text-muted-foreground">{story.tagline}</p>}

      <figure className="mt-6 m-0 overflow-hidden rounded-2xl border border-border bg-card">
        {story.single ? (
          <img
            src={story.before.url}
            srcSet={story.before.srcSet}
            sizes={story.before.sizes}
            width={story.before.width}
            height={story.before.height}
            alt={`${story.label} — weight-loss progress after bariatric surgery at Virginia Bariatric Surgery`}
            className="block h-auto w-full"
          />
        ) : (
          <div className="grid grid-cols-2">
            <img
              src={story.before.url}
              srcSet={story.before.srcSet}
              sizes={story.before.sizes}
              alt={`${story.label} before weight-loss surgery with Dr. Matthew Fitzer`}
              style={{ objectPosition: story.beforePosition ?? "center top" }}
              className={`${story.aspectClass ?? "aspect-[4/3]"} w-full object-cover`}
            />
            <img
              src={story.after.url}
              srcSet={story.after.srcSet}
              sizes={story.after.sizes}
              alt={`${story.label} after weight-loss surgery with Dr. Matthew Fitzer`}
              style={{ objectPosition: story.afterPosition ?? "center top" }}
              className={`${story.aspectClass ?? "aspect-[4/3]"} w-full object-cover`}
            />
          </div>
        )}
        <figcaption className="px-4 py-3 text-sm text-muted-foreground">
          {story.caption}
          <small className="mt-1 block text-xs text-muted-foreground/80">{storyCredit(story).creditLine}</small>
        </figcaption>
      </figure>

      {story.quote && (
        <blockquote className="mt-6 border-l-4 border-brand-logo pl-4 text-lg leading-[1.6] text-foreground/80">
          {story.quote}
        </blockquote>
      )}

      <div className="mt-8">
        <ShareButtons
          url={url}
          title={storyHeadline(story.label)}
          instagramImage={`${SITE}/og/stories/ig/${slug}.jpg`}
        />

      </div>

      <section className="mt-10 text-center">
        <h2 className="font-serif text-2xl">Ready to start your own before-and-after?</h2>
        <div className="mt-4 flex flex-wrap justify-center gap-3">
          <Link
            to="/appointments"
            className="rounded-full bg-brand-logo px-7 py-3 text-sm font-medium text-brand-foreground hover:opacity-90"
          >
            Request an Appointment
          </Link>
          <Link
            to="/results"
            className="rounded-full border border-brand-logo px-7 py-3 text-sm font-medium text-brand-logo hover:bg-brand-soft/50"
          >
            See All Patient Results
          </Link>
        </div>
      </section>
    </main>
  );
}
